import { useEffect, useState } from 'react';
import { DatabaseUserDocumentType } from '../../clients/quipli-service/user/user';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { InsuranceDocument, InsuranceDocumentRequest } from '../../clients/customer-api/customer/customer-customer';
import { InsuranceForm } from './insurance-form';
import { EcommerceButton } from '../../components/form/button';
import { ecommerceToast } from '../../util/toasts';
import { DateTime } from 'luxon';
import Icon from '../../components/icons/icon';
import { LegacyDownloadLink } from '../../components/link/legacy-download-link';
import { Typography } from '../../components/typography/typography';
import { displayMoneyFormat } from '../../util/util';
import classNames from 'classnames';
import { useCustomer } from '../../hooks/use-customer-context';

export const InsuranceFormWrapper = ({
  hideFile,
  isEditing,
  setIsEditing,
  setRootAttributes,
  wrapperClassName,
  includeEditButton = true,
  onSubmit,
}: {
  hideFile?: boolean;
  includeSkipButton?: boolean;
  isCompleted?: boolean;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  setRootAttributes?: (...args: any) => void;
  wrapperClassName?: string;
  includeEditButton?: boolean;
  onSubmit?: () => void;
}) => {
  const customer = useCustomer();
  const { addInsuranceDocument, updateInsuranceDocument, getInsuranceDocuments } = useCustomerAPI();
  const [insuranceDocument, setInsuranceDocument] = useState<InsuranceDocument>();

  async function init() {
    const docs = await getInsuranceDocuments();
    if (docs.length > 0) {
      setInsuranceDocument(docs[0]);
      onSubmit?.();
    }
  }
  useEffect(() => {
    customer && init();
  }, [customer]);

  async function addInsurance(insurance: Omit<InsuranceDocumentRequest, 'userId'>) {
    let added;
    try {
      if (customer) {
        added = await addInsuranceDocument(insurance);
        setInsuranceDocument(added);
        setIsEditing(false);

        ecommerceToast('Insurance document added', 'success');
        onSubmit?.();
        setRootAttributes?.({
          'data-saved': `true`,
        });
        return added;
      }
    } catch (e) {
      ecommerceToast('Error adding insurance document', 'error');
      return undefined;
    }
  }

  async function updateInsurance(documentId: number, body: Omit<InsuranceDocumentRequest, 'userId'>) {
    let updated;
    try {
      if (customer && documentId && insuranceDocument) {
        updated = await updateInsuranceDocument(documentId, body);
        setInsuranceDocument(updated);

        setIsEditing(false);
        ecommerceToast('Insurance document updated', 'success');
        onSubmit?.();
        setRootAttributes?.({
          'data-saved': `true`,
        });
        return updated;
      }
    } catch (e) {
      ecommerceToast('Error updating insurance document', 'error');
      return undefined;
    }
  }

  return customer ? (
    <div className="insurance-form-wrapper-v2">
      {!isEditing ? (
        <>
          {insuranceDocument ? (
            <>
              <InsuranceItem insurance={insuranceDocument} wrapperClassName={wrapperClassName} />
              {includeEditButton && (
                <div className="edit-button mt-4">
                  <EcommerceButton
                    id="add-edit-insurance-button"
                    onClick={() => setIsEditing(true)}
                    buttonStyle="tertiary"
                  >
                    Edit Insurance
                    <Icon icon="Edit" stroke="var(--quipli-black-700)" />
                  </EcommerceButton>
                </div>
              )}
            </>
          ) : (
            <div className="flex justify-between items-center p-3">
              <EcommerceButton id="add-edit-insurance-button" onClick={() => setIsEditing(true)} buttonStyle="tertiary">
                Add Insurance
              </EcommerceButton>
            </div>
          )}
        </>
      ) : (
        <InsuranceForm
          insuranceDocument={insuranceDocument}
          hideFile={hideFile}
          addInsuranceDocument={addInsurance}
          updateInsuranceDocument={updateInsurance}
          setRootAttributes={setRootAttributes}
          closeModal={() => {
            setIsEditing(false);
            if (insuranceDocument) {
              onSubmit?.();
            }
          }}
          onSubmit={() => {
            setIsEditing(false);
            init();
          }}
        />
      )}
    </div>
  ) : (
    <></>
  );
};

const InsuranceItem = ({
  insurance,
  wrapperClassName,
}: {
  insurance?: InsuranceDocument;
  wrapperClassName?: string;
}) => {
  return insurance?.id || insurance?.file ? (
    <div className={classNames(wrapperClassName)}>
      <div className="w-full md:w-1/2 flex flex-col gap-2">
        {insurance.file && (
          <>
            <div className="flex justify-between">
              <Typography type="text-sm" weight="bold" className="text-grey-500">
                Document name
              </Typography>

              <LegacyDownloadLink
                className="w-4/5 flex gap-3 items-center justify-end text-[var(--primary-color-500)] hover:text-[var(--primary-color-500)]"
                href={`users/download_file/${insurance.file.userId}/${DatabaseUserDocumentType.Insurance}`}
              >
                <Typography type="text-sm" weight="medium">
                  <span className="text-document-overflow">{insurance.file.fileName}</span>
                </Typography>
                <Icon icon="Download" size="medium" stroke="var(--primary-color-500)" />
              </LegacyDownloadLink>
            </div>

            <div className="flex justify-between">
              <Typography type="text-sm" weight="bold" className="text-grey-500">
                Uploaded at
              </Typography>

              <Typography type="text-sm" className="text-grey-600">
                <span>{`${DateTime.fromISO(insurance.file.createdAt, { setZone: true }).toFormat('DD t')}`}</span>
              </Typography>
            </div>
          </>
        )}
        {insurance.id && (
          <>
            <div className="flex justify-between">
              <Typography type="text-sm" weight="bold" className="text-grey-500">
                Full name
              </Typography>

              <Typography type="text-sm" className="text-grey-600">
                {insurance.firstName} {insurance.lastName}
              </Typography>
            </div>
            <div className="flex justify-between">
              <Typography type="text-sm" weight="bold" className="text-grey-500">
                Issued By
              </Typography>

              <Typography type="text-sm" className="text-grey-600">
                {insurance.issuedBy}
              </Typography>
            </div>
            <div className="flex justify-between">
              <Typography type="text-sm" weight="bold" className="text-grey-500">
                Policy Number
              </Typography>

              <Typography type="text-sm" className="text-grey-600">
                {insurance.policyNumber}
              </Typography>
            </div>
            <div className="flex justify-between">
              <Typography type="text-sm" weight="bold" className="text-grey-500">
                Expiry Date
              </Typography>

              <Typography type="text-sm" className="text-grey-600">
                {insurance.expiryDate!.toFormat('LLL d, yyyy')}
              </Typography>
            </div>
            <div className="flex justify-between">
              <Typography type="text-sm" weight="bold" className="text-grey-500">
                Issued Date
              </Typography>

              <Typography type="text-sm" className="text-grey-600">
                {insurance.issuedDate!.toFormat('LLL d, yyyy')}
              </Typography>
            </div>
            <div className="flex justify-between">
              <Typography type="text-sm" weight="bold" className="text-grey-500">
                Coverage Amount
              </Typography>

              <Typography type="text-sm" className="text-grey-600">
                {insurance.coverageAmount ? displayMoneyFormat(insurance.coverageAmount) : '-'}
              </Typography>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;
};
